"use client";

import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import { LIVE_LOCALE_CODES, localeName, type Locale } from "@wmart/i18n";
import { useTransition } from "react";

/**
 * Locale switcher — renders a <select> with endonyms (English / العربية / اردو).
 *
 * Preserves the current path + query string on switch. next-intl's
 * router.replace sets the NEXT_LOCALE cookie automatically so the preference
 * persists across navigations.
 *
 * Lives in the storefront (not @wmart/ui) so it can use next-intl hooks.
 * The header receives it as `localeSwitcherSlot`.
 */
export function LocaleSwitcher() {
  const t = useTranslations("LocaleSwitcher");
  const active = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  function onChange(next: Locale) {
    const qs = searchParams.toString();
    const href = qs ? `${pathname}?${qs}` : pathname;
    startTransition(() => {
      router.replace(href, { locale: next });
    });
  }

  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <span className="sr-only">{t("label")}</span>
      <select
        aria-label={t("label")}
        value={active}
        disabled={isPending}
        onChange={(e) => onChange(e.target.value as Locale)}
        className="cursor-pointer rounded-sm bg-transparent px-2 py-2 text-body-sm font-medium text-ink-700 outline-none hover:bg-accent-soft focus-visible:shadow-focus disabled:opacity-50"
      >
        {LIVE_LOCALE_CODES.map((code) => (
          <option key={code} value={code}>
            {localeName(code)}
          </option>
        ))}
      </select>
    </label>
  );
}
